package com.crowpay.views.components.project.work.scope

import com.crowpay.*
import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.completionResponse.CompletionFlowPermissions
import com.crowpay.completionResponse.CompletionResponse
import com.crowpay.completionResponse.serializable
import com.crowpay.extensions.actionName
import com.crowpay.extensions.confirmationText
import com.crowpay.extensions.isUnread
import com.crowpay.extensions.requiresAction
import com.crowpay.sdk.currentSession
import com.crowpay.sdk.lensUnread
import com.crowpay.sdk.notFoundError
import com.crowpay.sdk.notNullSession
import com.crowpay.utils.*
import com.crowpay.utils.tables.trackWeights
import com.crowpay.views.components.*
import com.crowpay.views.components.project.work.changeOrders.ChangeStatus
import com.crowpay.views.components.project.work.changeOrders.status
import com.crowpay.views.components.files.renderFiles
import com.crowpay.views.components.nav.viewIsNarrow
import com.crowpay.views.components.project.activity.concerns.IncidentReportForm
import com.crowpay.views.screens.common.ProjectLens
import com.crowpay.views.components.project.renderContractorNotes
import com.crowpay.views.components.project.work.punchList.renderPunchListRow
import com.crowpay.views.dialogs.*
import com.crowpay.views.screens.common.ProjectView
import com.crowpay.views.theming.*
import com.lightningkite.kiteui.models.*
import com.lightningkite.kiteui.navigation.dialogScreenNavigator
import com.lightningkite.kiteui.navigation.mainScreenNavigator
import com.lightningkite.kiteui.reactive.*
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.views.*
import com.lightningkite.kiteui.views.direct.*
import com.lightningkite.lightningdb.*
import com.lightningkite.now
import com.lightningkite.serialization.notNull
import kotlin.collections.map
import kotlin.time.Duration.Companion.seconds

fun ProjectView.renderLineItemDetails(
    item: Readable<AdjustedLineItem>,
    logs: Readable<List<ContractorNote>>,
    punchLists: Readable<List<PunchListItem>>,
    lineItemCompletionMessages: Readable<List<LineItemCompletionMessage>>,
    expanded: ImmediateWritable<Boolean>,
    writer: ViewWriter
) = with(writer) {
    sectionIndentCol {
        spacing = AppDimensions.sectionIndent * 1.5

        val pendingPayApp = shared {
            val line = item()._id
            payAppItems().any { it.lineItem == line && it.pending }
        }

        val pendingChange = shared {
            val line = item()._id
            pendingItemChanges().any { it.itemId == line && it.pending }
        }

        col {
            spacing = AppDimensions.sectionIndent
            space(0.1)
            lineItemBanners(item, lineItemCompletionMessages, pendingPayApp, pendingChange, lens)

            col {
                spacing = 2.px
                keyString("Description") { item().description }

                // Files
                label2("Files (images, Bids, Plans, Specs)") {
                    val files = item.lens { it.files }
                    existsDefaultFalse { files().isNotEmpty() }
                    renderFiles(files, AppDimensions.smallImagePreview)
                }
            }
        }


        workItemTasks(item, punchLists, project, lens)

        workItemNotes(item, expanded, logs, lens)

        workItemChangeLog(item, this)

        col {
            sizeConstraints(minHeight = AppDimensions.actionBarHeight) - darkSection - workItemActionBar(
                item,
                punchLists,
                project,
                lineItemCompletionMessages,
                pendingPayApp,
                pendingChange,
                lens
            )

            space()
        }
    }
}



fun ViewWriter.lineItemBanners(
    item: Readable<AdjustedLineItem>,
    messages: Readable<List<LineItemCompletionMessage>>,
    pendingPayApp: Readable<Boolean>,
    pendingChange: Readable<Boolean>,
    lens: ProjectLens,
    scope: Readable<ScopeViewInterface>? = null,
) {
    val unit = if (scope == null) "item" else "scope"

    val banners = shared {
        val item = item()
        val state = item.state
        val pendingPayApp = pendingPayApp()
        val pendingChange = pendingChange()

        if (scope == null && item.scopeSet == true) return@shared emptyList()

        banners {
            if (state == LineItemState.Cancelled) warn("This item has been cancelled, you can view its information but not edit it.")

            if (lens == ProjectLens.Contractor && state == LineItemState.InProgress && !pendingPayApp && !pendingChange) {
                info("Refer to original scope and change logs here for history of this $unit. Add notes and tasks for this work as desired.\n")
                warn("Don't request sign off before the $unit is completed. Doing so can build mistrust in your clients.")
            }

            if (lens == ProjectLens.Customer && state == LineItemState.InProgress) {
                info("Good communication ensures great results. Stay engaged with your contractor to clarify the work and ensure it aligns with your expectations. Asking questions now can prevent misunderstandings and keep the project on track.\n")
            }

            if (lens == ProjectLens.Contractor && pendingPayApp) {
                warn("This $unit is included in a pending draw. You can request sign-off once the draw is approved, rejected, or voided.")
            }

            if (lens == ProjectLens.Contractor && pendingChange) {
                warn("This $unit is included in a pending change. You can request sign-off once the change is approved, rejected, or withdrawn.")
            }

            if (lens == ProjectLens.Contractor && state == LineItemState.RequestingReview) {
                warn(
                    """
                        Your request for sign-off was sent on ${
                            messages().map { it.responseType }
                                .filterIsInstance<CompletionResponse.RequestComplete>()
                                .lastOrNull()?.accessInfo?.timestamp?.format(Formats.mmddyyyy_hmm)
                        }
                        
                        ${htmlKeyValue("Next Steps", "Keep communication clear and timely to ensure concerns are resolved quickly and approval is secured.")}
                    """.trimIndent()
                )
            }


            if (state == LineItemState.Complete && lens == ProjectLens.Customer)
                info(
                    """
                        Great news! ${scope?.invoke()?.scopeTitle ?: item().name} has been completed and approved.
                        
                        Your contractor will continue working on the project. Keep tracking updates in CrowPay, and remember to review and approve upcoming work items as sign-off is requested. Maintain open communication with your contractor to ensure a smooth process and avoid potential delays.
                    """.trimIndent()
                )

            if (state == LineItemState.Complete && lens == ProjectLens.Contractor)
                info(
                    """
                        Congratulations! ${scope?.invoke()?.scopeTitle ?: item().name} has been completed and approved.
                        
                        If any loose items remain, add them to the Project Punch List to keep everything organized for final closeout. Maintain clear communication with the homeowner to ensure alignment, manage expectations, and keep the project on track. Proactive updates build trust and help avoid unnecessary delays.
                    """.trimIndent()
                )
        }
    }

    renderBanners(banners)
}

fun ViewWriter.workItemTasks(
    item: Readable<AdjustedLineItem>,
    punchLists: Readable<List<PunchListItem>>,
    project: Readable<Project>,
    lens: ProjectLens,
) =
    renderPunchListRow(
        title = Constant("Tasks"),
        items = punchLists,
        project = project,
        lineItem = item,
        lens = lens,
        setTargets = true
    )


fun ViewWriter.workItemNotes(
    item: Readable<AdjustedLineItem>,
    itemExpanded: ImmediateWritable<Boolean>,
    notes: Readable<List<ContractorNote>>,
    lens: ProjectLens,
) {
    col {
        spacing = 0.px

        val logsExpanded = Property(false)
        val unread = currentSession.lensUnread { session ->
            session.contractorNotes.handleDelayUpdate { notes() }
        }

        leftExpandButtonWithUnread(logsExpanded, unread) {
            subTitle("Notes:")
        }

        onlyWhen { logsExpanded() } - sectionIndentCol {
            spacing = 1.rem
            space()
            if (lens == ProjectLens.Contractor) {
                atStart - tertiaryButton - button {
                    ::exists { !item().cancelled }
                    ::enabled { item().state.newNotesAllowed() }
                    spacing = 0.3.rem
                    specCenteredText("+ Work Note")
                    onClick {
                        val it = item()
                        dialogScreenNavigator.navigate(CreateOrEditNote(it.project, it._id, null))
                    }
                }
                space()
            }

            renderContractorNotes(
                notes,
                groupName = { item().name },
                lens,
                onJump = {
                    itemExpanded.value = true
                    logsExpanded.value = true
                }
            )
            space()
        }

        greySeparator()
    }
}


private val changeLogRowHeight = 3.rem
private fun ProjectView.workItemChangeLog(
    item: Readable<AdjustedLineItem>,
    writer: ViewWriter
) = with(writer) {
    fun WorkLogItem.status(): DisplayableState? = when (this) {
        is Credit -> null
        is ItemChange -> this.status
    }

    col {
        ::exists { item().let { it.changes.isNotEmpty() || it.wraps.changeRequest != null || it.wraps.credits.isNotEmpty() } }

        val priceChanges = item.lens { (it.changes + it.wraps.credits).sortedBy { log -> log.timestamp } }

        spacing = 0.px
        val logExpanded = Property(false)
        val unread = currentSession.lensUnread {
            it.itemChanges.handleDelayUpdate { item().changes } +
                    it.lineItems.handleCreditsDelayUpdate(1.seconds) { setOf(item().wraps) }
        }

        leftExpandButtonWithUnread(logExpanded, unread) { subTitle("Log") }

        onlyWhen { logExpanded() } - sectionIndentCol {
            trackWeights {
                space(1.rem)
                row {
                    setColWeight(3f) - bodyBold("Work Item")
                    setColWeight(1f) - bodyBold("ID") { align = Align.Center }
                    setColWeight(1f) - bodyBold("Amount") { align = Align.Center }
                    setColWeight(1f) - bodyBold("Status") { align = Align.Center }
                    setColWeight(1f) - bodyBold("Date") { align = Align.Center }
                    setColWeight(1f) - space()
                }
                col {
                    spacing = 0.px
                    greySeparator()
                    sizeConstraints(height = changeLogRowHeight) - row {
                        getColWeight - row {
                            spacing = AppDimensions.expandButtonSpace
                            centered - body { ::content { item().name } }
                            centered - smallBody {
                                ::content { if (item().wraps.changeRequest != null) "New" else "Original" }
                            }
                        }
                        getColWeight - centered - body {
                            align = Align.Center
                            ::content {
                                val info = item().changeRequest
                                if (info == null) "-"
                                else
                                    info.itemNumber

                            }
                        }
                        getColWeight - centered - body {
                            align = Align.Center
                            ::content { item().wraps.originalPrice.renderDollars() }
                        }
                        getColWeight - centered - body {
                            align = Align.Center
                            dynamicTheme {
                                if (item().wraps.changeRequest != null) ForegroundSemantic(ChangeStatus.Accepted.color)
                                else null
                            }
                            ::content { if (item().wraps.changeRequest != null) "Accepted" else "-" }
                        }
                        getColWeight - centered - body {
                            align = Align.Center
                            ::content { item().wraps.created.format(Formats.mmddyyyy) }
                        }
                        getColWeight - space()
                    }
                    greySeparator()
                    col {
                        spacing = 0.px
                        forEachUpdating(priceChanges) { change ->
                            col {
                                spacing = 0.px
                                sizeConstraints(height = changeLogRowHeight) - row {
                                    getColWeight - centered - row {
                                        spacing = AppDimensions.expandButtonSpace
                                        centered - unreadIconWhen { change().isUnread(notNullSession().userId) }
                                        centered - body { ::content { item().name } }
                                        centered - smallBody { ::content { change().type } }
                                    }
                                    getColWeight - centered - body {
                                        ::content { change().id ?: "-" }
                                        align = Align.Center
                                    }
                                    getColWeight - stack {
                                        centered - renderPriceChange { change().changeAmount }
                                    }
                                    getColWeight - centered - body {
                                        align = Align.Center
                                        dynamicTheme { change().status()?.let(::DisplayableStateSemantic) }
                                        ::content { change().status()?.displayName ?: "-" }
                                    }
                                    getColWeight - centered - body {
                                        align = Align.Center
                                        ::content { change().timestamp.format(Formats.mmddyyyy) }
                                    }
                                    getColWeight - stack {
                                        centered - secondaryButton - button {
                                            ::visible {
                                                when (val c = change()) {
                                                    is Credit -> c.lineItem != null || c.incident != null || c.projectCreditGroup != null
                                                    is ItemChange -> true
                                                }
                                            }
                                            specCenteredText("View")
                                            onClick("View Change Details") {
                                                when (val c = change()) {
                                                    is Credit -> {
                                                        val page = c.lineItem
                                                            ?.let { id ->
                                                                val line = lineItems().find { it._id == id } ?: notFoundError<LineItem>()
                                                                LineItemCompletionForm.createDialog(line, project(), lens)
                                                            }
                                                            ?: c.incident?.let { id ->
                                                                val incident = incidents().find { it._id == id } ?: notFoundError<Incident>()
                                                                IncidentReportForm.createDialog(project(), incident, incidentMessages(), lens)
                                                            }
                                                            ?: project().projectCredits.find { it.projectCreditGroup == c.projectCreditGroup }?.let {
                                                                ProjectCreditDetails(
                                                                    it,
                                                                    client(),
                                                                    contractor(),
                                                                    notNullSession().userId,
                                                                    project()
                                                                )
                                                            }

                                                        page?.let(dialogScreenNavigator::navigate)
                                                    }
                                                    is ItemChange -> JumpTo.ChangeItem(c._id)
                                                }
                                            }
                                        }
                                    }
                                }
                                greySeparator()
                            }
                        }
                    }
                }
            }
        }

        greySeparator { ::exists { !logExpanded() } }
    }
}

fun ViewWriter.workItemActionBar(
    item: Readable<AdjustedLineItem>,
    punchLists: Readable<List<PunchListItem>>,
    project: Readable<Project>,
    lineItemCompletionMessages: Readable<List<LineItemCompletionMessage>>,
    pendingPayApp: Readable<Boolean>,
    pendingChange: Readable<Boolean>,
    lens: ProjectLens,
) =
    stack {
        spacing = AppDimensions.buttonRowSpacing

        val (primaryAction, secondaryAction) = lineItemActions(
            item,
            punchLists,
            project,
            lineItemCompletionMessages,
            pendingPayApp,
            pendingChange,
            lens
        )

        exists = false
        existsDefaultFalse { (primaryAction() != null || secondaryAction() != null) && (item().scopeSet != true) }

        atEnd - rowCollapsingToColumn(AppDimensions.normalCollapseWidth) {
            spacing = AppDimensions.buttonRowSpacing

            button {
                applyReactiveActionFormatting(secondaryAction)
            }

            if (lens == ProjectLens.Contractor) {
                centered - onlyWhen(false) { pendingPayApp() } - infoBubble("Unavailable — item is in a draw under review. Void or wait to enable sign-off.")
                centered - onlyWhen(false) { pendingChange() } - infoBubble("Unavailable — item is in a change request under review. Withdraw or wait to enable sign-off.")
            }

            button {
                applyReactiveActionFormatting(primaryAction)
            }
//            space(AppDimensions.sectionIndent / 2 - AppDimensions.cornerRadii)
        }
    }

fun ViewWriter.lineItemActions(
    item: Readable<AdjustedLineItem>,
    punchLists: Readable<List<PunchListItem>>,
    project: Readable<Project>,
    lineItemCompletionMessages: Readable<List<LineItemCompletionMessage>>,
    payAppPending: Readable<Boolean>,
    pendingChange: Readable<Boolean>,
    lens: ProjectLens,
): Pair<Readable<ButtonAction?>, Readable<ButtonAction?>> {
    val unreadMessages = shared {
        lineItemCompletionMessages().isUnread(notNullSession().userId)
    }
    val primaryAction = shared {
        val project = project()
        val lineItem = item()

        val isCustomer = (lens != ProjectLens.Contractor)
        val isContractor = (lens == ProjectLens.Contractor)
        val notComplete = (lineItem.state < LineItemState.Complete)
        val started = (lineItem.started != null)
        val notOnHold = lineItem.state != LineItemState.OnHold
        val notInScopeSet = lineItem.scopeSet != true

        val permissions = CompletionFlowPermissions(lineItem.wraps, isCustomer, lineItemCompletionMessages())
        val previousMessage = permissions.previousResponse

        when {
            isContractor &&
                    notComplete &&
                    !started &&
                    notInScopeSet &&
                    project.state in setOf(ProjectState.PendingStart, ProjectState.InProgress) ->
                ButtonAction("Start Work") {
                    fun confirmation(
                        header: String,
                        message: String,
                        confirmationText: String,
                    ) = GenericConfirmationDialog(
                        header = header,
                        message = message,
                        confirmationText = confirmationText,
                        confirmationTheme = PrimaryButtonSemantic,
                        onSubmit = {
                            if (it) {
                                val session = notNullSession()

                                if (lineItem.scopeSet == true) {
                                    session.lineItems.bulkModify(
                                        MassModification(
                                            condition<LineItem> {
                                                if (lineItem.changeRequest != null) it.changeRequest.notNull.id eq lineItem.changeRequest!!.id
                                                else it.scopeView eq lineItem.scopeView
                                            } and condition { it.state eq lineItem.state },
                                            modification { it.started assign now() }
                                        )
                                    )
                                } else {
                                    session
                                        .lineItems[lineItem._id]
                                        .modify(modification { it.started assign now() })
                                }
                            }
                        }
                    )

                    dialogScreenNavigator.navigate(
                        when {
                            project.started == null && lineItem.scopeSet == true -> confirmation(
                                "Start Items and Project?",
                                "Starting this work item will start the project and other items in the scope set as well",
                                "Start Items and Project"
                            )

                            project.started == null -> confirmation(
                                "Start Item and Project?",
                                "Starting this work item will start the project as well",
                                "Start Item and Project"
                            )

                            lineItem.scopeSet == true -> confirmation(
                                "Start Work Items?",
                                "Starting this work item will start the other items in the scope set as well",
                                "Start Work Items"
                            )

                            else -> confirmation(
                                "Start Work Item?",
                                "",
                                "Start Work Item"
                            )
                        }
                    )
                }

            isCustomer &&
                    notComplete &&
                    notOnHold &&
                    notInScopeSet &&
                    permissions.canAccept ->
                ButtonAction(
                    permissions.mostRecentCompletingProposal?.actionName() ?: ""
                ) {
                    val proposal = permissions.mostRecentProposal ?: return@ButtonAction
                    val text = proposal.confirmationText(lineItem.wraps)
                    dialogScreenNavigator.navigate(
                        GenericConfirmationDialog(
                            header = text.first,
                            message = text.second,
                            confirmationText = text.third
                        ) {
                            if (!it) return@GenericConfirmationDialog
                            val session = notNullSession()
                            session.nonCached.lineItemCompletionMessage.sendMessage(
                                LineItemCompletionMessage(
                                    project = project._id,
                                    lineItem = lineItem._id,
                                    sender = session.userId,
                                    clientMessage = lens.isClient(),
                                    responseTypeSerializable = CompletionResponse.Accept(
                                        proposal,
                                        session.currentAccess()
                                    ).serializable(),
                                    message = ""
                                )
                            )
                        }
                    )
                }

            isContractor &&
                    notComplete &&
                    project.state == ProjectState.InProgress &&
                    notInScopeSet &&
                    lineItem.state in setOf(
                LineItemState.InProgress,
                LineItemState.KeepWorking,
                LineItemState.WorkingOnRemedy
            ) ->
                ButtonAction(
                    "Request Sign Off",
                    enabled = { !payAppPending() && !pendingChange() }
                ) {
                    if (punchLists.awaitSuspending().any { it.required && it.complete == null })
                        dialogScreenNavigator.navigate(
                            GenericDialog(
                                message = "You still have required punch list items pending. You cannot request review until all required punch list items are complete.",
                            )
                        )
                    else {
                        dialogScreenNavigator.navigate(
                            WorkItemRequestSignOffDialog(listOf(item.awaitSuspending()), lineItemCompletionMessages.awaitSuspending())
                        )
                    }
                }

            lineItem.state in setOf(LineItemState.Complete, LineItemState.Resolved) ->
                ButtonAction.withUnread("View History", theme = SecondaryButtonSemantic, unread = { unreadMessages() }) {
                    val navigator = if (viewIsNarrow) mainScreenNavigator else dialogScreenNavigator
                    navigator.navigate(
                        LineItemCompletionForm.createDialog(lineItem, project(), lens)
                            .apply { historyExpanded.value = true }
                    )
                }

            else -> null
        }
    }

    val secondaryAction = shared {
        val project = project()
        val lineItem = item()

        val isClient = (lens != ProjectLens.Contractor)
//        val isContractor = (lens == ProjectLens.Contractor)
//        val notComplete = (lineItem.state < LineItemState.Complete)
//        val notDisputed = (project.state != ProjectState.Disputed)
//        val notInScopeSet = lineItem.scopeSet != true

        val permissions = CompletionFlowPermissions(
            lineItem.wraps,
            lens != ProjectLens.Contractor,
            lineItemCompletionMessages()
        )

        when {
            lineItem.state > LineItemState.InProgress && lineItem.state < LineItemState.Resolved ->
                ButtonAction.withUnreadAndAction(
                    if (lineItem.state == LineItemState.RequestingReview || permissions.previousResponse?.responseType is CompletionResponse.RequestSiteVisit) {
                        if (permissions.awaitingResponse) "Review Request"
                        else "View Request"
                    } else when {
                        permissions.awaitingResponse && permissions.previousResponse?.responseType is CompletionResponse.MoreInfo -> "Provide Info"

                        isClient && permissions.run {
                            awaitingResponse && mostRecentProposal != null && mostRecentProposal != mostRecentCompletingProposal
                        } -> permissions.mostRecentProposal!!.actionName()

                        permissions.awaitingResponse -> "Review Concern"
                        else -> "View Concern"
                    },
                    theme = SecondaryButtonSemantic,
                    unread = { unreadMessages() },
                    actionIcon = {
                        permissions.requiresAction()
                    },
                ) {
                    if (viewIsNarrow) mainScreenNavigator.navigate(LineItemCompletionForm.createDialog(lineItem, project, lens))
                    else dialogScreenNavigator.navigate(LineItemCompletionForm.createDialog(lineItem, project, lens))
                }


            else -> null
        }
    }

    return Pair(primaryAction, secondaryAction)
}